// Settings panel — Text alerts to my phone.
//
// The operator opts in to SMS alerts delivered to THEIR OWN cell, sent FROM
// their in-system CRM number, and chooses which events buzz their phone:
//   • someone books on their calendar
//   • their AI agent hands off a conversation (with a summary)
//   • a new lead comes in
//   • they miss a call
//   • someone texts them
//
// Backed by /api/notifications/operator-prefs (GET/PUT) → migration 229
// operator_notification_prefs. Master is OFF by default (opt-in). Delivery
// defaults to the cell captured at signup unless overridden here.

import React from 'react';
import { Smartphone, RefreshCcw, BellRing } from 'lucide-react';
import { supabase } from '../lib/supabase';

const EVENT_TYPES = [
  { key: 'notify_appointment_booked', label: 'Someone books on my calendar', hint: 'AI-set, referral, or manual bookings.' },
  { key: 'notify_inbound_sms',        label: 'A text needs me',             hint: 'Texts from unknown numbers your AI won’t handle. Known-lead chats buzz you only via the AI hand-off alert below.' },
  { key: 'notify_missed_call',        label: 'I miss a call',               hint: 'Inbound calls you didn’t pick up.' },
  { key: 'notify_new_lead',           label: 'A new lead comes in',         hint: 'Vendor and Facebook/Meta lead inflow.' },
  { key: 'notify_ai_summary',         label: 'My AI agent hands off a chat', hint: 'A summary when the AI escalates to you.' },
];

const DEFAULTS = {
  sms_enabled: false,
  delivery_phone_e164: '',
  notify_appointment_booked: true,
  notify_inbound_sms: true,
  notify_missed_call: true,
  notify_new_lead: true,
  notify_ai_summary: true,
  quiet_start_hour: null,
  quiet_end_hour: null,
  timezone: '',
};

// The operator's actual browser timezone — used to render appointment times
// and honor the overnight pause in their real local zone. Best-effort.
function browserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  v: h,
  label: h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`,
}));

async function jwt() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || '';
}

export default function OperatorTextAlertsCard({ currentUser, showToast = () => {} }) {
  const [form, setForm]       = React.useState(DEFAULTS);
  const [signupMobile, setSignupMobile] = React.useState(null);
  const [effective, setEffective]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]       = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const token = await jwt();
      const res = await fetch('/api/notifications/operator-prefs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const p = data?.prefs || {};
      setForm({
        ...DEFAULTS,
        ...Object.fromEntries(Object.keys(DEFAULTS).map(k => [k, k in p && p[k] != null ? p[k] : DEFAULTS[k]])),
        delivery_phone_e164: p.delivery_phone_e164 || '',
        // Adopt the operator's real browser tz when none is stored yet, so the
        // first save captures it for time rendering + the pause window.
        timezone: p.timezone || browserTz(),
      });
      setSignupMobile(data?.signupMobile || null);
      setEffective(data?.effectiveDeliveryPhone || null);
    } catch (err) {
      showToast(`Couldn’t load alert settings: ${err.message}`);
    }
    setLoading(false);
  }, [currentUser?.id, showToast]);

  React.useEffect(() => { load(); }, [load]);

  const pauseOn = form.quiet_start_hour != null && form.quiet_end_hour != null;

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const token = await jwt();
      const res = await fetch('/api/notifications/operator-prefs', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'save failed');
      setEffective(data?.effectiveDeliveryPhone || null);
      setSignupMobile(data?.signupMobile || null);
      showToast('Text alert settings saved.');
    } catch (err) {
      showToast(`Save failed: ${err.message}`);
    }
    setBusy(false);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const token = await jwt();
      const res = await fetch('/api/notifications/operator-prefs', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await res.json();
      if (data?.ok && data?.dryRun) showToast('Test queued (sending is in dry-run on this environment).');
      else if (data?.ok) showToast(`Test text sent to ${data.to || 'your cell'} — check your phone.`);
      else if (data?.reason === 'no_delivery_phone') showToast('Add your cell number above and Save first, then send a test.');
      else if (data?.reason === 'blocked') showToast(`Couldn’t send: ${(data.blockers || []).join(', ')}.`);
      else showToast('Test could not be sent. Check your number and try again.');
    } catch (err) {
      showToast(`Test failed: ${err.message}`);
    }
    setTesting(false);
  };

  if (loading) {
    return <div className="bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-500">Loading text alert settings…</div>;
  }

  const noCellAnywhere = !form.delivery_phone_e164 && !effective;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BellRing size={16} className="text-amber-600" />
          <h3 className="font-bold text-slate-900">Text alerts to my phone</h3>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-700" title="Reload"><RefreshCcw size={14} /></button>
      </div>
      <p className="text-xs text-slate-500 -mt-1">
        Get a text on your personal cell — sent from your own CRM number — the moment something happens.
        You pick exactly what buzzes you. Off by default.
      </p>

      {/* Master toggle */}
      <label className="flex items-start gap-3 cursor-pointer rounded-lg bg-slate-50 border border-slate-200 p-3">
        <input
          type="checkbox"
          checked={!!form.sms_enabled}
          onChange={e => set({ sms_enabled: e.target.checked })}
          disabled={busy}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
        />
        <span className="text-sm">
          <span className="font-semibold text-slate-900">Send me text alerts</span>
          <br />
          <span className="text-xs text-slate-500">The master switch. When off, nothing is sent no matter the choices below.</span>
        </span>
      </label>

      {/* Delivery phone */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Send alerts to</label>
        <div className="flex items-center gap-2">
          <Smartphone size={15} className="text-slate-400 shrink-0" />
          <input
            type="tel"
            value={form.delivery_phone_e164 || ''}
            onChange={e => set({ delivery_phone_e164: e.target.value })}
            placeholder={effective || signupMobile || '+1 555 555 0100 (your personal cell)'}
            disabled={busy}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white font-mono"
          />
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          {form.delivery_phone_e164
            ? 'Alerts go to this number.'
            : effective
              ? <>Blank — using the cell from your signup: <span className="font-mono">{effective}</span>.</>
              : 'No cell on file yet. Enter your personal number so alerts have somewhere to land.'}
        </div>
        {noCellAnywhere && form.sms_enabled && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
            Alerts are on but no delivery number is set — add your cell above or they won’t send.
          </div>
        )}
      </div>

      {/* Event type toggles */}
      <div className={`space-y-2 ${form.sms_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Text me when…</div>
        {EVENT_TYPES.map(t => (
          <label key={t.key} className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form[t.key]}
              onChange={e => set({ [t.key]: e.target.checked })}
              disabled={busy || !form.sms_enabled}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm text-slate-700 leading-tight">
              {t.label}
              <br />
              <span className="text-[11px] text-slate-400">{t.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {/* Overnight pause */}
      <div className={`${form.sms_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={pauseOn}
            onChange={e => set(e.target.checked
              ? { quiet_start_hour: 22, quiet_end_hour: 7 }
              : { quiet_start_hour: null, quiet_end_hour: null })}
            disabled={busy || !form.sms_enabled}
            className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
          />
          <span className="text-slate-700">Pause alerts overnight (your local time)</span>
        </label>
        {pauseOn && (
          <div className="flex items-center gap-2 mt-2 text-sm pl-6">
            <span className="text-slate-500 text-xs">From</span>
            <select value={form.quiet_start_hour} onChange={e => set({ quiet_start_hour: Number(e.target.value) })}
              disabled={busy} className="border border-slate-300 rounded px-2 py-1 text-sm bg-white">
              {HOURS.map(h => <option key={h.v} value={h.v}>{h.label}</option>)}
            </select>
            <span className="text-slate-500 text-xs">to</span>
            <select value={form.quiet_end_hour} onChange={e => set({ quiet_end_hour: Number(e.target.value) })}
              disabled={busy} className="border border-slate-300 rounded px-2 py-1 text-sm bg-white">
              {HOURS.map(h => <option key={h.v} value={h.v}>{h.label}</option>)}
            </select>
          </div>
        )}
        <div className="text-[10px] text-slate-500 mt-1 pl-6">
          Urgent alerts (a text, a missed call) still respect this window — it’s your personal quiet time.
          {form.timezone ? <> Times use <span className="font-mono">{form.timezone}</span>.</> : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
        <button
          onClick={sendTest}
          disabled={testing || busy}
          className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
          title="Sends one text to your saved number so you can confirm alerts arrive."
        >
          {testing ? 'Sending…' : 'Send me a test'}
        </button>
        <button
          onClick={save}
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save alert settings'}
        </button>
      </div>
    </div>
  );
}
